import { planArrangement } from "../shared/agent-layout";
import { duplicateTrace } from "./tracing";
import { editableTarget } from "./tracing";
import type { Editor, VecLike } from "./canvas";
import type { TLShape, TLShapeId } from "@tldraw/tlschema";
import { nativeDocument } from "../shared/document-session";
import { duplicateWorkspaceDocument } from "./workspace-documents";
import type { BoardDocument, Sheet } from "../shared/schema";

export const SHEET_CAN_RESIZE = true;

export function sheetAt(editor: Editor, point: VecLike) {
  return editor
    .getCurrentPageShapesSorted()
    .slice()
    .reverse()
    .find((s): s is Sheet => {
      if (s.type !== "sheet" || s.isLocked) return false;
      const p = editor.getPointInShapeSpace(s, point);
      return p.x >= 0 && p.y >= 0 && p.x <= s.props.w && p.y <= s.props.h;
    });
}
export function imagePlacement(editor: Editor, point?: VecLike) {
  const selected = editor.getOnlySelectedShape();
  const sheet =
    !point && selected?.type === "trace" && editableTarget(editor, selected)
      ? selected
      : !point && selected?.type === "sheet" && !selected.isLocked
        ? selected
        : sheetAt(editor, point ?? editor.getViewportPageBounds().center);
  const center =
    point ??
    (sheet
      ? editor.getShapePageBounds(sheet)!.center
      : editor.getViewportPageBounds().center);
  return {
    parentId: sheet?.id ?? editor.getCurrentPageId(),
    center: sheet ? editor.getPointInShapeSpace(sheet, center) : center,
  };
}
export function orderedSheets(editor: Editor) {
  return editor
    .getCurrentPageShapes()
    .filter((s): s is Sheet => s.type === "sheet")
    .sort(
      (a, b) =>
        Number(a.meta.order ?? 1e9) - Number(b.meta.order ?? 1e9) ||
        a.index.localeCompare(b.index) ||
        a.id.localeCompare(b.id),
    );
}
export function reorderSheet(editor: Editor, id: TLShapeId, direction: number) {
  const sheets = orderedSheets(editor),
    index = sheets.findIndex((s) => s.id === id),
    to = index + direction;
  if (index < 0 || to < 0 || to >= sheets.length) return;
  [sheets[index], sheets[to]] = [sheets[to], sheets[index]];
  editor.markHistoryStoppingPoint("Reorder sheets");
  editor.updateShapes(
    sheets.map((s, i) => ({
      id: s.id,
      type: s.type,
      meta: { ...s.meta, order: i },
    })),
  );
}
export function attachToSheet(editor: Editor, ids: TLShapeId[], sheet: Sheet) {
  const shapes = ids
    .map((id) => editor.getShape(id))
    .filter((s): s is TLShape => !!s);
  if (
    sheet.isLocked ||
    shapes.some(
      (s) =>
        s.type === "sheet" ||
        s.type === "mark" ||
        s.type === "trace" ||
        nativeDocument(s),
    )
  )
    throw new Error(
      "Move ordinary images, text or drawings; measured marks stay with their source.",
    );
  editor.markHistoryStoppingPoint("Move to sheet");
  editor.reparentShapes(shapes, sheet.id);
}
export function tidyGrid(editor: Editor) {
  const shapes = editor.getSelectedShapes();
  if (
    !shapes.length ||
    shapes.some(
      (s) =>
        s.parentId !== shapes[0].parentId ||
        s.type === "sheet" ||
        s.type === "mark" ||
        s.type === "trace",
    )
  )
    throw new Error("Select ordinary objects belonging to the same sheet.");
  const store = Object.fromEntries(
    editor.store.allRecords().map((r) => [r.id, r]),
  );
  const measured = {
    revision: 0,
    bounds: shapes.map((s) => {
      const b = editor.getShapeGeometry(s).bounds;
      return { id: s.id, x: b.x, y: b.y, w: b.w, h: b.h };
    }),
  };
  const updates = planArrangement(store, shapes, {
    mode: "grid",
    gap: 24,
    measured,
  });
  editor.markHistoryStoppingPoint("Arrange in grid");
  editor.updateShapes(
    updates.map((u) => ({
      id: u.id as TLShapeId,
      type: editor.getShape(u.id as TLShapeId)!.type,
      x: u.x,
      y: u.y,
    })),
  );
}

/** Find a clear position to the right, including repeated duplication/paste. */
export function nextSheetPosition(
  editor: Editor,
  width: number,
  height: number,
  x: number,
  y: number,
) {
  const gap = 48;
  let left = x + width + gap;
  const sheets = orderedSheets(editor);
  for (let pass = 0; pass <= sheets.length; pass++) {
    const overlap = sheets.find((s) => {
      const b = editor.getShapePageBounds(s)!;
      return (
        left < b.maxX + gap &&
        left + width > b.minX - gap &&
        y < b.maxY + gap &&
        y + height > b.minY - gap
      );
    });
    if (!overlap) break;
    left = editor.getShapePageBounds(overlap)!.maxX + gap;
  }
  return { x: left, y };
}
export function duplicateSelection(editor: Editor) {
  const selected = editor.getSelectedShapes();
  if (selected.length === 1 && nativeDocument(selected[0])) {
    duplicateWorkspaceDocument(editor, selected[0] as BoardDocument);
    return;
  }
  if (selected.length === 1 && selected[0].type === "trace") {
    duplicateTrace(editor, selected[0]);
    return;
  }
  const sheet =
    selected.length === 1 && selected[0].type === "sheet"
      ? selected[0]
      : undefined;
  const bounds = sheet && editor.getShapePageBounds(sheet);
  const to =
    bounds && nextSheetPosition(editor, bounds.w, bounds.h, bounds.x, bounds.y);
  editor.markHistoryStoppingPoint("Duplicate selection");
  editor.duplicateShapes(
    editor.getSelectedShapeIds(),
    to && bounds
      ? { x: to.x - bounds.x, y: to.y - bounds.y }
      : { x: 24, y: 24 },
  );
  if (sheet)
    editor.zoomToBounds(editor.getSelectionPageBounds()!, {
      inset: 180,
      animation: { duration: 200 },
    });
}
